import type { TimedMetadataItem } from '@mimir/common/dto/timedMetadataModels';
import type { TimedMdfUsageRule } from '@mimir/common/dto/timedMdfUsage';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { FieldValue } from '@mimir/common/dto/fieldValue';
import type { Item } from '@mimir/common/dto/searchResultHit';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import type { ItemMediaPlayerOps } from './itemMediaPlayerOps';
import type Timeline from '@mimir/client-common/timeline/Timeline';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import { isPropertyEmpty } from '@mimir/common/util/general';
import { getRestrictedFormUsageRules } from '@mimir/web/util/usageRestrictionsCheck';
import { getUsageRuleFromTimedMdf } from '@mimir/common/timed-mdf/usage-rules';


export interface TimedMetadataItemModel extends TimedMetadataItem {
  swimlane: number;
  hasValidationErrors: boolean;
  isNewNonSavedItem: boolean;
  usageRule?: TimedMdfUsageRule;
  timecode?: string;
  selected?: boolean;
}

export const ITEM_HEIGHT = 10;

export interface TimedMdfState {
  timedMdfSaving: boolean;
  savingItemIds: ReadonlySet<string>;
  collectionItems: TimedMetadataItemModel[];
  selectedItem: TimedMetadataItemModel | null;
  hoveredItem: TimedMetadataItemModel | null;
  loadingCollection: boolean;
  loadedCollection: boolean;
  itemsWithChanges: string[];
  shouldAutoFocusSelectedItem: boolean;
  timedMdfs: MetaDataSchema[];
  remoteUpdateCount: number;
}

export function timedMdfCollectionSorted(state: Readonly<TimedMdfState>): TimedMetadataItemModel[] {
  return sortBy(state.collectionItems, [item => item.startMs]);
}

export function timedMdfUsageRules(services: TimedMdfServices): TimedMdfUsageRule[] | undefined {
  const mdfs = services.getTimedMdfs();
  return getRestrictedFormUsageRules(mdfs);
}

export interface SetSelectedTimedMdfItemPayload {
  item: TimedMetadataItem | null;
  setStart: boolean;
  shouldAutoFocusSelectedItem: boolean;
  playerOps: ItemMediaPlayerOps;
}

export interface AddTimedMdfPayload {
  mdf: MetaDataSchema;
  timecode?: string;
  playerOps: ItemMediaPlayerOps;
}

export interface CopyTimedMdfPayload {
  oldTimedMdfItem: TimedMetadataItem;
  playerOps: ItemMediaPlayerOps;
}

export interface SaveTimedMdfItemsPayload {
  timedItemIds: string[];
  abortController: AbortController;
}

export interface TimedMdfOps {
  loadTimedMdf(): Promise<void>;
  loadForItem(itemId: string): Promise<void>;
  cleanup(): void;

  setSelectedItem(payload: SetSelectedTimedMdfItemPayload): void;
  setHovered(item: TimedMetadataItemModel | null): void;
  setShouldAutoFocusSelectedItem(value: boolean): void;
  setSelectedItemHasErrors(hasErrors: boolean): void;

  add(info: AddTimedMdfPayload): void;
  addItem(item: TimedMetadataItemModel): void;
  remove(item: TimedMetadataItemModel): void;
  copyTimedMdf(payload: CopyTimedMdfPayload): void;

  itemHasChanged(timedItemId: string, hasChanged: boolean): void;
  updateItemsWithSnapshot(): void;
  getSnapshotEntry(timedItemId: string): TimedMetadataItemModel | undefined;

  saveItems(payload: SaveTimedMdfItemsPayload): Promise<void>;

  setSelectedTimedMdf(mdf: MetaDataSchema | null): void;
  setLoadedCollection(loaded: boolean): void;

  applyRemoteDelete(id: string): void;
  applyFullRemoteEntry(id: string, fetched: TimedMetadataItem): void;
  mergeRemoteEntry(id: string, fetched: TimedMetadataItem): void;
  incrementRemoteUpdateCount(): void;
}

export interface TimedMdf {
  state: Readonly<TimedMdfState>;
  ops: TimedMdfOps;
}

export interface TimedMdfServices {
  getTimedMdfs(): MetaDataSchema[];
  getOptionLists(): OptionListDto[];
  getCurrentUserId(): string;
  getCurrentItem(): Item | null;
  getTimeline(): Timeline;
  clearTimelineState(): void;
}

/**
 * Three-way merge for timed metadata entries.
 * Uses the snapshot (base) to determine what the user changed locally,
 * then preserves those changes while applying remote updates to untouched fields.
 * User wins on fields changed by both sides.
 */
export function mergeRemoteIntoLocal(
  base: TimedMetadataItemModel,
  local: TimedMetadataItemModel,
  remote: TimedMetadataItem,
): TimedMetadataItemModel {
  // Scalars: if user changed it (local ≠ base), keep user's value. Otherwise use remote.
  const startMs = local.startMs !== base.startMs ? local.startMs : remote.startMs;
  const endMs = local.endMs !== base.endMs ? local.endMs : remote.endMs;
  const generated_summary = local.generated_summary !== base.generated_summary
    ? local.generated_summary
    : remote.generated_summary;
  const formId = local.data.formId !== base.data.formId
    ? local.data.formId
    : remote.data.formId;

  const mergedFormData: Record<string, FieldValue> = { ...remote.data.formData };
  for (const fieldId of Object.keys(local.data.formData)) {
    const baseVal = base.data.formData[fieldId];
    const localVal = local.data.formData[fieldId];
    if (isPropertyEmpty(baseVal) && isPropertyEmpty(localVal)) continue;
    if (!isEqual(localVal, baseVal)) {
      mergedFormData[fieldId] = localVal;
    }
  }
  for (const fieldId of Object.keys(base.data.formData)) {
    if (!(fieldId in local.data.formData) && fieldId in base.data.formData) {
      delete mergedFormData[fieldId];
    }
  }

  return {
    ...local,
    startMs,
    endMs,
    generated_summary,
    data: { formId, formData: mergedFormData },
    lastModified: remote.lastModified ?? local.lastModified,
  };
}

export function dtoToModel(dto: TimedMetadataItem, usageRules?: TimedMdfUsageRule[], timecode?: string): TimedMetadataItemModel {
  return {
    hasValidationErrors: false,
    isNewNonSavedItem: false,
    swimlane: 0,
    timecode,
    ...cloneDeep(dto),
    ...(usageRules && { usageRule: getUsageRuleFromTimedMdf(dto, usageRules) }),
  };
}
